// components/brand/BrandForm/PromotionSection.js - Fixed Image constructor conflict
import React, { useState, useMemo } from 'react';
import { PlusCircle, X, Move, Image as ImageIcon, Edit3, Bold, Type, List, Hash } from 'lucide-react'; // Renamed Image import
import ImageUploader from '../../ImageUploader';
import { sanitizeBrandName } from '../../../utils/imageUpload';
import { 
  ALL_GAME_CATEGORIES, 
  getCategoryIcon, 
  getCategoryColor 
} from '../../../lib/gameCategory';

// Maximum image dimension for promotions
const MAX_PROMO_IMAGE_DIMENSION = 800;
// Maximum file size for promotion images (1MB)
const MAX_FILE_SIZE = 1024 * 1024;

// Hardcoded promotion categories
const HARDCODED_CATEGORIES = [
    'Welcome Bonus',
    'Reload (daily/weekly)',
    'Rebate (by turnover)',
    'Losing Rescue',
    'Winning Bonus',
    'Birthday',
    'Referral',
    'Tournaments',
    'Others'
];

function PromotionSection({ promotions = [], onChange, brandId = null, brandName = null }) {
  const [activeCategory, setActiveCategory] = useState('Welcome Bonus');
  const [descriptionModal, setDescriptionModal] = useState({ isOpen: false, promotionId: null, value: '' });

  // Generate a consistent temp ID for new brands (for Firebase Storage folder structure)
  const [tempBrandId] = useState(() => brandId || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`);
  const effectiveBrandId = brandId || tempBrandId;
  const folderName = sanitizeBrandName(brandName, effectiveBrandId);

  // Get all unique categories from existing promotions + hardcoded ones
  const allCategories = useMemo(() => {
    const existingCategories = [...new Set(promotions.map(p => p.category).filter(Boolean))];
    const combinedCategories = [...HARDCODED_CATEGORIES];
    
    // Add any existing categories that aren't in hardcoded list
    existingCategories.forEach(category => {
      if (!HARDCODED_CATEGORIES.includes(category)) {
        combinedCategories.push(category);
      }
    });
    
    return combinedCategories;
  }, [promotions]);

  // Check if category is hardcoded or auto-created
  const isHardcodedCategory = (category) => {
    return HARDCODED_CATEGORIES.includes(category);
  };

  // Group promotions by category
  const groupedPromotions = useMemo(() => {
    const grouped = {};
    allCategories.forEach(category => {
      grouped[category] = promotions.filter(p => p.category === category);
    });
    return grouped;
  }, [promotions, allCategories]);

  // Set initial active category if current one doesn't exist
  React.useEffect(() => {
    if (!allCategories.includes(activeCategory)) {
      setActiveCategory(allCategories[0] || 'Welcome Bonus');
    }
  }, [allCategories, activeCategory]);


  // Handle adding a new promotion to a category
  const handleAddPromotion = (category) => {
    const updatedPromotions = [...promotions];
    updatedPromotions.push({
      id: Date.now().toString(), // Use timestamp as unique ID
      category: category,
      title: '',
      description: '',
      image: '',
      createdAt: new Date()
    });
    onChange(updatedPromotions);
  };

  // Handle removing a promotion
  const handleRemovePromotion = (promotionId) => {
    const updatedPromotions = promotions.filter(p => p.id !== promotionId);
    onChange(updatedPromotions);
  };

  // Handle updating a promotion field
  const handleUpdatePromotion = (promotionId, field, value) => {
    const updatedPromotions = promotions.map(p => {
      if (p.id === promotionId) {
        return { ...p, [field]: value };
      }
      return p;
    });
    onChange(updatedPromotions);
  };

  // Handle description modal
  const openDescriptionModal = (promotionId, currentValue) => {
    setDescriptionModal({
      isOpen: true,
      promotionId,
      value: currentValue || ''
    });
  };

  const closeDescriptionModal = () => {
    setDescriptionModal({ isOpen: false, promotionId: null, value: '' });
  };

  const saveDescription = () => {
    if (descriptionModal.promotionId) {
      handleUpdatePromotion(descriptionModal.promotionId, 'description', descriptionModal.value);
    }
    closeDescriptionModal();
  };

  // Rich text formatting functions
  const insertFormatting = (format) => {
    const textarea = document.getElementById('description-textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = descriptionModal.value.substring(start, end);
    let formattedText = '';

    switch (format) {
      case 'h2':
        formattedText = `## ${selectedText || 'Heading 2'}`;
        break;
      case 'h3':
        formattedText = `### ${selectedText || 'Heading 3'}`;
        break;
      case 'bold':
        formattedText = `**${selectedText || 'bold text'}**`;
        break;
      case 'bullet':
        if (selectedText) {
          // Split selected text by lines and add bullets to each non-empty line
          const lines = selectedText.split('\n');
          formattedText = lines
            .map(line => line.trim() ? `• ${line.trim()}` : line)
            .join('\n');
        } else {
          formattedText = '• bullet point';
        }
        break;
      case 'numbered':
        if (selectedText) {
          // Split selected text by lines and add numbered bullets to each non-empty line
          const lines = selectedText.split('\n');
          let counter = 1;
          formattedText = lines
            .map(line => line.trim() ? `${counter++}. ${line.trim()}` : line)
            .join('\n');
        } else {
          formattedText = '1. numbered point';
        }
        break;
      default:
        return;
    }

    const newValue = 
      descriptionModal.value.substring(0, start) + 
      formattedText + 
      descriptionModal.value.substring(end);

    setDescriptionModal(prev => ({ ...prev, value: newValue }));

    // Set cursor position after the inserted text
    setTimeout(() => {
      const newCursorPos = start + formattedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  // Convert markdown-like syntax to HTML for preview
  const formatDescriptionForDisplay = (text, truncate = false) => {
    if (!text) return '';
    
    let processedText = text;
    
    // Truncate text if requested (for preview in form)
    if (truncate && text.length > 150) {
      processedText = text.substring(0, 150) + '...';
    }
    
    return processedText
      // H2 headings - reduced spacing
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-red-800 mb-2 mt-2">$1</h2>')
      // H3 headings - reduced spacing
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-red-800 mb-2 mt-2">$1</h3>')
      // Bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-red-800">$1</strong>')
      // Bullet points
      .replace(/^• (.+)$/gm, '<div class="flex items-start mb-1"><span class="text-red-600 font-bold mr-2 mt-0.5">•</span><span class="text-gray-700">$1</span></div>')
      // Numbered points
      .replace(/^(\d+)\. (.+)$/gm, '<div class="flex items-start mb-1"><span class="text-red-600 font-bold mr-2 mt-0.5">$1.</span><span class="text-gray-700">$2</span></div>')
      // Convert remaining single newlines to spaces for paragraph flow
      .replace(/\n/g, ' ')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ');
  };

  // Handle promotion image change
  const handlePromotionImageChange = async (promotionId, finalUrl) => {
    if (!finalUrl) {
      // If image was removed
      handleUpdatePromotion(promotionId, 'image', '');
      return;
    }

    // finalUrl is now either a Firebase Storage URL or base64 (for compatibility)
    handleUpdatePromotion(promotionId, 'image', finalUrl);
    console.log(`Promotion image updated successfully: ${finalUrl}`);
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center">
        <h4 className="text-lg font-medium text-gray-700">Promotions</h4>
        <div className="relative ml-2 group">
          <svg className="h-5 w-5 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="absolute left-0 bottom-6 w-80 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
            <div className="font-medium mb-2">Promotion Image Tips:</div>
            <ul className="space-y-1 text-gray-200">
              <li>• Each image represents a separate promotion</li>
              <li>• Supported formats: JPG, PNG, GIF, WEBP</li>
              <li>• Images will be automatically compressed to fit 1MB limit</li>
              <li>• Use high-quality images for better results</li>
            </ul>
          </div>
        </div>
      </div>
      
      {/* Category Tabs */}
      <div className="flex overflow-x-auto pb-2 border-b border-gray-200">
        {allCategories.map(category => {
          const isHardcoded = isHardcodedCategory(category);
          return (
            <button
              key={category}
              type="button" 
              onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                setActiveCategory(category);
              }}
              className={`px-4 py-2 whitespace-nowrap transition-colors mr-2 rounded-t-lg ${
                activeCategory === category
                  ? isHardcoded 
                    ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600 font-medium' // Hardcoded active
                    : 'bg-green-50 text-green-600 border-b-2 border-green-600 font-medium' // Auto-created active
                  : isHardcoded
                    ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50' // Hardcoded inactive
                    : 'text-gray-600 hover:text-green-600 hover:bg-green-50' // Auto-created inactive
              }`}
            >
              {category}
              {!isHardcoded && (
                <span className="ml-1 text-xs bg-green-100 text-green-700 rounded px-1">*</span>
              )}
              <span className={`ml-2 rounded-full text-xs px-2 py-1 ${
                isHardcoded ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'
              }`}>
                {groupedPromotions[category]?.length || 0}
              </span>
            </button>
          );
        })}
      </div>
      
      {/* Legend for tab colors */}
      <div className="flex items-center space-x-4 text-xs text-gray-500">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded mr-1"></div>
          <span>Standard Categories</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-100 border border-green-300 rounded mr-1"></div>
          <span>Custom Categories (*)</span>
        </div>
      </div>
      
      {/* Active Category Promotions */}
      <div className="mt-4">
        <div className="flex justify-between items-center mb-4">
          <h5 className="font-medium text-gray-700">{activeCategory} Promotions</h5>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault(); // Prevent form submission
              handleAddPromotion(activeCategory);
            }}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add
          </button>
        </div>
        
        {!groupedPromotions[activeCategory] || groupedPromotions[activeCategory].length === 0 ? (
          <div className="bg-gray-50 p-8 rounded-lg border border-gray-200 text-center">
            <ImageIcon className="mx-auto h-10 w-10 text-gray-400 mb-2" />
            <p className="text-gray-500">No promotions added yet.</p>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault(); // Prevent form submission
                handleAddPromotion(activeCategory);
              }}
              className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Your First Promotion
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedPromotions[activeCategory].map((promotion, index) => (
              <div key={promotion.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-start mb-2">
                  <span className="inline-flex items-center text-xs bg-blue-100 text-blue-700 rounded-full px-2.5 py-1">
                    <Move className="mr-1 h-3 w-3" />
                    Promotion {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent form submission
                      handleRemovePromotion(promotion.id);
                    }}
                    className="text-red-500 hover:text-red-700 inline-flex items-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Image and Game Categories Row */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Image Upload - 2 columns (wider) */}
                    <div className="md:col-span-2">
                      <ImageUploader
                        label=""
                        id={`promotion-image-${promotion.id}`}
                        initialImageUrl={promotion.image || ''}
                        onImageSelect={(finalUrl) => handlePromotionImageChange(promotion.id, finalUrl)}
                        maxSizeMB={2}
                        previewHeight="max-h-48"
                        accept="image/jpeg, image/png, image/jpg, image/gif, image/webp"
                        storageConfig={{
                          folderPath: `brands/${folderName}/promotions`
                        }}
                      />
                    </div>
                    
                    {/* Game Categories - 1 column */}
                    <div className="md:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Game Categories
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {ALL_GAME_CATEGORIES.map(gameCategory => {
                          const IconComponent = getCategoryIcon(gameCategory);
                          return (
                            <label key={gameCategory} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={(promotion.gameCategories || []).includes(gameCategory)}
                                onChange={(e) => {
                                  const currentCategories = promotion.gameCategories || [];
                                  let updatedCategories;
                                  
                                  if (e.target.checked) {
                                    // Add category if checked
                                    updatedCategories = [...currentCategories, gameCategory];
                                  } else {
                                    // Remove category if unchecked
                                    updatedCategories = currentCategories.filter(cat => cat !== gameCategory);
                                  }
                                  
                                  handleUpdatePromotion(promotion.id, 'gameCategories', updatedCategories);
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700 flex items-center">
                                {IconComponent && <IconComponent className="w-3 h-3 mr-1" />}
                                {gameCategory}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {promotion.gameCategories && promotion.gameCategories.length > 0 && (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {promotion.gameCategories.map(category => {
                              const IconComponent = getCategoryIcon(category);
                              const colorClass = getCategoryColor(category);
                              return (
                                <span
                                  key={category}
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
                                >
                                  {IconComponent && <IconComponent className="w-3 h-3 mr-1" />}
                                  {category}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {/* Category Dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          value={promotion.category || ''}
                          onChange={(e) => handleUpdatePromotion(promotion.id, 'category', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select category...</option>
                          {/* Hardcoded categories first */}
                          <optgroup label="Standard Categories">
                            {HARDCODED_CATEGORIES.map(category => (
                              <option key={category} value={category}>
                                {category}
                              </option>
                            ))}
                          </optgroup>
                          {/* Auto-created categories */}
                          {allCategories.filter(cat => !isHardcodedCategory(cat)).length > 0 && (
                            <optgroup label="Custom Categories">
                              {allCategories
                                .filter(cat => !isHardcodedCategory(cat))
                                .map(category => (
                                  <option key={category} value={category}>
                                    {category} *
                                  </option>
                                ))}
                            </optgroup>
                          )}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Title
                        </label>
                        <input
                          type="text"
                          value={promotion.title || ''}
                          onChange={(e) => handleUpdatePromotion(promotion.id, 'title', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Promotion title"
                        />
                      </div>

                      {/* Max Bonus IDR Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Bonus IDR
                        </label>
                        <input
                          type="number"
                          value={promotion.maxBonus || ''}
                          onChange={(e) => handleUpdatePromotion(promotion.id, 'maxBonus', parseFloat(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter max bonus amount"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      {/* Turnover Needed Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Turnover Needed
                        </label>
                        <input
                          type="number"
                          value={promotion.turnoverNeeded || ''}
                          onChange={(e) => handleUpdatePromotion(promotion.id, 'turnoverNeeded', parseFloat(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter turnover requirement"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <div
                          onClick={() => openDescriptionModal(promotion.id, promotion.description)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:bg-gray-50 min-h-[76px] flex items-start justify-between"
                        >
                          <div className="flex-1">
                            {promotion.description ? (
                              <div 
                                className="text-gray-900 text-sm"
                                dangerouslySetInnerHTML={{ 
                                  __html: formatDescriptionForDisplay(promotion.description, true) 
                                }}
                              />
                            ) : (
                              <p className="text-gray-500 text-sm">Click to add promotion description...</p>
                            )}
                          </div>
                          <Edit3 className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Description Modal */}
      {descriptionModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-full max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Promotion Description</h3>
              <button
                type="button"
                onClick={closeDescriptionModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-6 flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              
              {/* Rich Text Toolbar */}
              <div className="flex items-center space-x-2 mb-3 p-2 bg-gray-50 rounded-lg border">
                <button
                  type="button"
                  onClick={() => insertFormatting('h2')}
                  className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Heading 2"
                >
                  <Type className="h-4 w-4 mr-1" />
                  H2
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('h3')}
                  className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Heading 3"
                >
                  <Type className="h-3 w-3 mr-1" />
                  H3
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('bold')}
                  className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Bold"
                >
                  <Bold className="h-4 w-4 mr-1" />
                  Bold
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('bullet')}
                  className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Bullet Point"
                >
                  <List className="h-4 w-4 mr-1" />
                  Bullet
                </button>
                <button
                  type="button"
                  onClick={() => insertFormatting('numbered')}
                  className="flex items-center px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  title="Numbered List"
                >
                  <Hash className="h-4 w-4 mr-1" />
                  Number
                </button>
                <div className="text-xs text-gray-500 ml-4">
                  Select text and click formatting buttons, or use markdown syntax
                </div>
              </div>

              <textarea
                id="description-textarea"
                value={descriptionModal.value}
                onChange={(e) => setDescriptionModal(prev => ({ ...prev, value: e.target.value }))}
                className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 resize-none font-mono text-sm"
                placeholder="Enter detailed promotion description...

Examples:
## Main Heading
### Sub Heading
**Bold text in dark red**
• Bullet point item
• Another bullet point
1. First numbered item
2. Second numbered item

Use the toolbar buttons above or type markdown syntax directly."
                autoFocus
              />

              <p className="mt-2 text-sm text-gray-500">
                Use ## for H2 headings, ### for H3 headings, **text** for bold formatting, • for bullet points, and 1. for numbered lists.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                type="button"
                onClick={closeDescriptionModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveDescription}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save Description
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromotionSection;